import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { ip_back } from "../config";

function authHeaders() {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: token } : {}),
  };
}

async function recapRequest(tsId, { method = "GET", body, userId } = {}) {
  const params = new URLSearchParams();
  if (userId) params.set("user_id", userId);
  const qs = params.toString() ? `?${params}` : "";
  const resp = await fetch(
    `${ip_back}api/session-recap/${encodeURIComponent(tsId)}${qs}`,
    {
      method,
      headers: authHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    }
  );
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok && resp.status !== 202) {
    const error = new Error(json.message || `Request failed (${resp.status})`);
    error.code = json.code || null;
    throw error;
  }
  return json.data;
}

function segmentLabel(item) {
  if (item.kind === "photo") return "Photo";
  if (item.kind === "feedback") return "Coach feedback";
  if (item.kind === "still") return item.label || "Still";
  return "Exercise clip";
}

function playerKey(player) {
  return String(
    player?.user_id ||
      player?.userId ||
      player?.id ||
      player?.player_id ||
      ""
  );
}

/**
 * Coach generates a session or individual player recap (max 30s).
 * Individual recaps need captured video for that player and include
 * feedback text, photos, and videos with overlays baked in.
 */
export function SessionRecapDialog({
  open,
  onClose,
  tsId,
  selectedPlayerId = "all",
  filterPlayers = [],
  t = (k, d) => d || k,
}) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [excluded, setExcluded] = useState({});
  const [copied, setCopied] = useState(false);
  const [targetUserId, setTargetUserId] = useState(null);

  const selectedFromFilter = useMemo(() => {
    if (!selectedPlayerId || selectedPlayerId === "all") return null;
    const key = String(selectedPlayerId);
    const match = (filterPlayers || []).find((p) => {
      const candidates = [
        p?._filterKey,
        p?.user_id,
        p?.userId,
        p?.player_id,
        p?.id,
      ]
        .filter((v) => v != null)
        .map(String);
      return candidates.includes(key);
    });
    const userId = match?.user_id || match?.userId || null;
    return userId ? String(userId) : null;
  }, [selectedPlayerId, filterPlayers]);

  useEffect(() => {
    if (!open) return;
    setTargetUserId(selectedFromFilter);
    setExcluded({});
    setCopied(false);
    setError(null);
  }, [open, selectedFromFilter]);

  const load = useCallback(async () => {
    if (!tsId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await recapRequest(tsId, { userId: targetUserId || undefined });
      setInfo(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tsId, targetUserId]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const rendering = info?.share?.status === "rendering";

  useEffect(() => {
    if (!open || !rendering) return undefined;
    const timer = window.setInterval(load, 4000);
    return () => window.clearInterval(timer);
  }, [open, rendering, load]);

  const players = useMemo(() => {
    const fromApi = Array.isArray(info?.players) ? info.players : [];
    if (fromApi.length) return fromApi;
    return (filterPlayers || [])
      .map((p) => ({
        user_id: playerKey(p),
        display_name:
          [p?.user_first_name || p?.first_name, p?.user_last_name || p?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          p?.display_name ||
          p?.name ||
          playerKey(p),
        video_count: null,
        consent: null,
      }))
      .filter((p) => p.user_id);
  }, [info?.players, filterPlayers]);

  const generate = async () => {
    if (!tsId) return;
    setBusy(true);
    setError(null);
    try {
      const excludeIds = Object.entries(excluded)
        .filter(([, off]) => off)
        .map(([id]) => id);
      const body = { exclude_ids: excludeIds };
      if (targetUserId) body.user_id = targetUserId;
      const share = await recapRequest(tsId, {
        method: "POST",
        body,
        userId: targetUserId || undefined,
      });
      setInfo((prev) => ({ ...(prev || {}), share }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    const url = info?.share?.share_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const shareFile = async () => {
    const url = info?.share?.video_url;
    if (!url) return;
    const fileName = targetUserId ? "player-recap.mp4" : "session-recap.mp4";
    if (navigator.share && navigator.canShare) {
      try {
        const blob = await fetch(url).then((r) => r.blob());
        const file = new File([blob], fileName, { type: "video/mp4" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: targetUserId ? "Player recap" : "Session recap",
          });
          return;
        }
      } catch {
        /* fall through */
      }
    }
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.target = "_blank";
    anchor.rel = "noopener";
    anchor.click();
  };

  const timeline = info?.timeline || [];
  const ready = info?.share?.status === "ready" && info?.share?.video_url;
  const isPlayer = !!targetUserId;
  const selectedPlayerMeta = players.find((p) => String(p.user_id) === String(targetUserId));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        {isPlayer
          ? t("playerRecap", "Player recap")
          : t("sessionRecap", "Session recap")}
        <IconButton
          aria-label={t("close", "Close")}
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !info ? (
          <CircularProgress size={28} />
        ) : (
          <>
            <p style={{ marginTop: 0 }}>
              {isPlayer
                ? t(
                    "playerRecapHelp",
                    "Only a coach can generate this. It is a personal 30-second video for the player from their captured clips, photos, and coach feedback. Overlays are burned into the file."
                  )
                : t(
                    "sessionRecapHelp",
                    "One video, 30 seconds at most, made from this session's exercise clips, staff photos, and stills. Knee angle, stroke phase, and contact frames are burned into the file."
                  )}
            </p>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
                {t("recapTarget", "Who receives this recap")}
              </label>
              <select
                value={targetUserId || ""}
                disabled={rendering || busy}
                onChange={(event) => {
                  const next = event.target.value || null;
                  setTargetUserId(next);
                  setExcluded({});
                  setCopied(false);
                }}
                style={{ width: "100%", padding: "8px 10px" }}
              >
                <option value="">
                  {t("wholeSessionRecap", "Whole session")}
                </option>
                {players.map((player) => (
                  <option key={player.user_id} value={player.user_id}>
                    {player.display_name}
                    {player.video_count != null
                      ? ` (${player.video_count} video${player.video_count === 1 ? "" : "s"})`
                      : ""}
                    {player.consent === false ? " — no consent" : ""}
                  </option>
                ))}
              </select>
            </div>

            {isPlayer && selectedPlayerMeta?.consent === false ? (
              <p>
                {t(
                  "playerRecapNoConsent",
                  "This player has not granted public media consent, so a shareable recap cannot be generated."
                )}
              </p>
            ) : null}
            {isPlayer && info?.reason === "no_player_video" ? (
              <p>
                {t(
                  "playerRecapNoVideo",
                  "No video was captured for this player in the session."
                )}
              </p>
            ) : null}
            {info && info.finalized === false ? (
              <p>
                {t(
                  "sessionRecapNeedsFinal",
                  "Finalize the session before generating the recap."
                )}
              </p>
            ) : null}
            {!isPlayer && info?.blocked_exercises > 0 ? (
              <p>
                {t(
                  "sessionRecapConsent",
                  "Some exercises are left out because public media consent is missing."
                )}
              </p>
            ) : null}
            {timeline.length ? (
              <ul style={{ paddingLeft: 18 }}>
                {timeline.map((item) => (
                  <li key={item.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={!excluded[item.id]}
                        disabled={rendering || busy}
                        onChange={(event) =>
                          setExcluded((prev) => ({
                            ...prev,
                            [item.id]: !event.target.checked,
                          }))
                        }
                      />{" "}
                      {segmentLabel(item)} · {item.seconds}s
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p>
                {isPlayer
                  ? t(
                      "playerRecapEmpty",
                      "No clips, photos, or feedback for this player can go in a recap yet."
                    )
                  : t(
                      "sessionRecapEmpty",
                      "No clips or photos from this session can go in a public recap yet."
                    )}
              </p>
            )}
            {rendering ? (
              <p>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t("sessionRecapRendering", "Generating the recap…")}
              </p>
            ) : null}
            {info?.share?.status === "failed" ? (
              <p>
                {info.share.error_message ||
                  t("sessionRecapFailed", "The recap could not be generated.")}
              </p>
            ) : null}
            {ready ? (
              <>
                <video
                  controls
                  playsInline
                  poster={info.share.thumbnail_url || undefined}
                  src={info.share.video_url}
                  style={{ width: "100%", borderRadius: 8, background: "#000" }}
                />
                <p>
                  {t(
                    "sessionRecapLinkNote",
                    "Pasting the link into WhatsApp shows a preview. The video plays in WhatsApp's browser. Send the video file if you want it to play inside the chat."
                  )}
                </p>
              </>
            ) : null}
            {error ? <p>{error}</p> : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {ready ? (
          <>
            <Button onClick={copyLink}>
              {copied ? t("copied", "Copied") : t("copyLink", "Copy link")}
            </Button>
            <Button onClick={shareFile}>
              {t("sendVideoFile", "Send video file")}
            </Button>
          </>
        ) : null}
        <Button
          variant="contained"
          onClick={generate}
          disabled={
            busy ||
            rendering ||
            info?.finalized === false ||
            !timeline.length ||
            (isPlayer && selectedPlayerMeta?.consent === false)
          }
        >
          {isPlayer
            ? t("generatePlayerRecap", "Generate player recap")
            : t("generateRecap", "Generate recap")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SessionRecapDialog;
